import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { ROUTE_MARKETPLACE } from 'constants/routes';
import { Typography, Box, Button, Theme } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';
import { css } from '@emotion/css';
import Tilt from 'react-parallax-tilt';
import DisplayOn from 'shared/components/DisplayOn';
import Picture from 'shared/components/Picture';
import { useStyles } from '../styles';

const sliderStyles = makeStyles()((theme: Theme) => {
	return {
		root: {
			paddingLeft: 12,
			paddingRight: 12,
			paddingBottom: 24,
			position: 'relative',

			'& .MuiTypography-root': {
				color: '#FFFFFF',
			},
			'& .MuiButton-root': {
				width: '100%',
				whiteSpace: 'nowrap',
			},
			'& .MuiButton-contained': {
				background: '#FFFFFF',
				color: '#3889FA',
			},
			'& .MuiButton-outlined': {
				background: 'transparent',
				color: '#FFFFFF',
				border: '2px solid #FFFFFF',
				boxShadow: 'none',
				backgroundImage: 'none',
				'&:before, &:after': {
					display: 'none',
				},
			},
			[theme.breakpoints.up('sm')]: {
				paddingLeft: 21,
				'& .MuiButton-root': {
					width: 124,
					fontWeight: 500,
					fontSize: '1.2rem',
					lineHeight: '1.5rem',
				},
			},
			[theme.breakpoints.up('lg')]: {
				paddingLeft: 67,
				'& .MuiButton-root': {
					width: 180,
					fontWeight: 600,
					fontSize: '1.6rem',
					lineHeight: '2.4rem',
				},
			},
		},
		imageBox: {
			position: 'absolute',
			left: 0,
			top: -60,
			width: '100%',
			height: '100%',
			zIndex: -1,

			'& picture': {
				width: '100%',
				height: '100%',
				'& img': {
					width: '100%',
					height: 'auto',
					objectFit: 'contain',
				},
			},
			[theme.breakpoints.up('sm')]: {
				top: -40,
			},
			[theme.breakpoints.up('lg')]: {
				top: 0,
			},
		},
		text: {
			fontSize: '2.4rem',
			fontWeight: 600,
			lineHeight: '3.2rem',

			[theme.breakpoints.up('sm')]: {
				fontSize: '2.8rem',
				lineHeight: '3.6rem',
			},
			[theme.breakpoints.up('lg')]: {
				fontSize: '4.8rem',
				fontWeight: 700,
				lineHeight: '6rem',
			},
		},
		actionsBox: {
			[theme.breakpoints.between('xs', 'sm')]: {
				display: 'grid',
				gridTemplateColumns: 'repeat(2, 1fr)',
				gridTemplateRows: 'repeat(2, 1fr)',
				gridColumnGap: 32,
				gridRowGap: 16,
			},
			[theme.breakpoints.up('sm')]: {
				display: 'flex',
				'& .MuiButton-root ~ .MuiButton-root': {
					marginLeft: theme.spacing(4),
				},
			},
		},
		startBtn: {
			[theme.breakpoints.between('xs', 'sm')]: {
				gridArea: '1 / 1 / 2 / 3',
				width: '100%',
			},
		},
		connectBtn: {
			[theme.breakpoints.between('xs', 'sm')]: {
				gridArea: '2 / 1 / 3 / 2',
				'& ~ .MuiButton-root': {
					gridArea: '2 / 2 / 3 / 3',
				},
			},
		},
		bsptBtn: {
			[theme.breakpoints.between('xs', 'sm')]: {
				gridArea: '2 / 1 / 3 / 3',
			},
		},
		tokenBadge: {
			position: 'absolute',
			transform: 'rotate(15deg) scale(.7)',
			top: 40,
			right: -72,

			[theme.breakpoints.up('sm')]: {
				transform: 'scale(1)',
				top: 72,
				right: -46,
			},
			[theme.breakpoints.up('lg')]: {
				right: 192,
				top: 85,
			},
		},
		athletsBadge: {
			position: 'absolute',
			transform: 'rotate(15deg) scale(.68)',
			top: 18,
			left: -57,

			[theme.breakpoints.up('sm')]: {
				transform: 'rotate(-15deg)',
				top: 60,
				left: 13,
			},
			[theme.breakpoints.up('lg')]: {
				top: 38,
				right: -19,
				left: 'auto',
			},
		},
		nftBadge: {
			position: 'absolute',
			transform: 'rotate(-30deg) scale(0.67)',
			top: -44,
			left: 56,

			[theme.breakpoints.up('sm')]: {
				transform: 'rotate(-30deg)',
				top: 48,
				left: 216,
			},
			[theme.breakpoints.up('md')]: {
				top: 48,
				left: '34vw',
			},
			[theme.breakpoints.up('lg')]: {
				transform: 'rotate(18.13deg)',
				bottom: 12,
				right: -2,
				left: 'auto',
				top: 'auto',
			},
		},
	};
});

const Banner2 = (props: any) => {
	const { isConnected, handleConnect } = props;
	const { classes, cx } = useStyles();
	const { classes: bannerClasses } = sliderStyles();
	const theme = useTheme();

	return (
		<Box display="flex" flexDirection="column" justifyContent={{ xs: 'flex-end', lg: 'center' }} className={cx(classes.baseSlide, bannerClasses.root)}>
			<Box className={bannerClasses.imageBox}>
				<Picture
					alt="Teams retro banner"
					srcSet={[
						{
							media: `(min-width: ${theme.breakpoints.values.xs}px) and (max-width: ${theme.breakpoints.values.sm - 1}px)`,
							img: 'https://prod-blocksport-nft-data.s3.eu-central-1.amazonaws.com/assets/banners/Banner2Background-mobile.png',
						},
						{
							media: `(min-width: ${theme.breakpoints.values.sm}px) and (max-width: ${theme.breakpoints.values.lg - 1}px)`,
							img: 'https://prod-blocksport-nft-data.s3.eu-central-1.amazonaws.com/assets/banners/Banner2Background-tablet.png',
						},
						{
							media: `(min-width: ${theme.breakpoints.values.lg}px)`,
							img: 'https://prod-blocksport-nft-data.s3.eu-central-1.amazonaws.com/assets/banners/Banner2Background.png',
							fallback: true,
						},
					]}
				/>
			</Box>
			<Box className={bannerClasses.tokenBadge}>
				<Tilt gyroscope={true} trackOnWindow>
					<img src="https://prod-blocksport-nft-data.s3.eu-central-1.amazonaws.com/assets/banners/Token%20Badge.png" alt="Token Badge" />
				</Tilt>
			</Box>
			<Box className={bannerClasses.athletsBadge}>
				<Tilt gyroscope={true} trackOnWindow>
					<img src="https://prod-blocksport-nft-data.s3.eu-central-1.amazonaws.com/assets/banners/Athlets%20Badge.png" alt="Athlets Badge" />
				</Tilt>
			</Box>
			<Box className={bannerClasses.nftBadge}>
				<Tilt gyroscope={true} trackOnWindow>
					<img src="https://prod-blocksport-nft-data.s3.eu-central-1.amazonaws.com/assets/banners/NFT%20Badge.png" alt="NFT Badge" />
				</Tilt>
			</Box>
			<Box mb={3}>
				<Typography variant="h2" className={bannerClasses.text}>
					{'Discover top Sports NFTs\n& Fan Tokens in one place'}
				</Typography>
			</Box>
			<DisplayOn media={theme.breakpoints.up('lg')}>
				<Box mb={7}>
					<Typography variant="h3" className={css({ fontSize: '2rem', fontWeight: 500, lineHeight: '3.2rem' })}>
						{
							'Create, issue and distribute any non-fungible tokens\nfor Sports Collectors. Monetize your fan base via a custom\nFan Token Offering. Play to earn with integrated BSPT, NFTs\nand Fan Tokens into DeFi games.'
						}
					</Typography>
				</Box>
			</DisplayOn>
			<Box className={bannerClasses.actionsBox}>
				<Button variant="contained" component={RouterLink} to={`${ROUTE_MARKETPLACE}`} className={bannerClasses.startBtn}>
					Start Collecting
				</Button>
				{isConnected || (
					<Button variant="outlined" onClick={handleConnect} className={bannerClasses.connectBtn}>
						Connect Wallet
					</Button>
				)}
				<Button variant="outlined" component={RouterLink} to={{ pathname: 'https://www.mexc.com/exchange/BSPT_USDT' }} target="_blank" className={bannerClasses.bsptBtn}>
					Buy BSPT
				</Button>
			</Box>
		</Box>
	);
};

export default Banner2;
